/**
 * Blackbaud API usage — the shape both read surfaces answer in.
 *
 * The super-admin tab and the `sj` verb consume THIS, rather than each shaping
 * the same rows their own way, because the failure it prevents is not a type
 * error: it is two surfaces quietly disagreeing about how much quota a school
 * has used. The top-endpoint cutoff is exported here for the same reason — a
 * different cutoff on either side makes their "heaviest endpoints" lists
 * incomparable while both look correct.
 */

import { z } from 'zod'

/** Blackbaud's daily request quota, enforced per school connection. */
export const BLACKBAUD_DAILY_QUOTA = 100_000

/**
 * How many endpoints a day's breakdown names before the rest is rolled into an
 * explicit `otherRequests` total. One constant, both surfaces (KTD6).
 */
export const BLACKBAUD_USAGE_TOP_ENDPOINTS = 5

/** How the count for a school-day came to exist. */
export const BlackbaudUsageProvenanceSchema = z.enum(['live', 'seeded', 'mixed'])
export type BlackbaudUsageProvenance = z.infer<typeof BlackbaudUsageProvenanceSchema>

export const BlackbaudUsageEndpointSchema = z.object({
  endpoint: z.string().describe('Normalized endpoint template, ids collapsed to :id / :uuid / :cuid'),
  requestCount: z.number().int().nonnegative()
})
export type BlackbaudUsageEndpoint = z.infer<typeof BlackbaudUsageEndpointSchema>

export const BlackbaudUsageDaySchema = z.object({
  date: z.string().describe('UTC day, YYYY-MM-DD, aligned to Blackbaud’s own quota reset'),
  totalRequests: z.number().int().nonnegative(),
  /**
   * Usage against the 100,000/day quota, so the number carries meaning without
   * the reader knowing the limit (R18). Null for the unattributed bucket, which
   * belongs to no school and therefore to no quota.
   */
  quotaPercent: z.number().nullable(),
  topEndpoints: z.array(BlackbaudUsageEndpointSchema),
  /** Everything outside `topEndpoints`, stated rather than dropped. */
  otherRequests: z.number().int().nonnegative(),
  provenance: BlackbaudUsageProvenanceSchema,
  /**
   * True when the count is known to be incomplete — either the counting path
   * lost writes (R20) or the day was reconstructed from logs, which can only
   * ever be read as a floor (R14). `provenance` says which.
   */
  isLowerBound: z.boolean(),
  /** False for a day still accumulating, which is the normal state of today (R16). */
  isFinalized: z.boolean()
})
export type BlackbaudUsageDay = z.infer<typeof BlackbaudUsageDaySchema>

/**
 * Endpoint value of the row that exists only to carry a school-day's
 * lower-bound mark. It always has a zero count and is never an endpoint anyone
 * called, so it is excluded from every breakdown.
 */
export const BLACKBAUD_USAGE_LOSS_MARKER_ENDPOINT = '(unrecorded)'

/** One stored row, as either read surface receives it. */
export interface BlackbaudUsageStoredRow {
  /** `YYYY-MM-DD`, or a Date the caller has not narrowed yet. */
  usageDate: string | Date
  endpoint: string
  source: string
  requestCount: number
  isLowerBound: boolean
  finalizedAt: Date | string | null
}

function toDayKey(value: string | Date): string {
  return typeof value === 'string' ? value.slice(0, 10) : value.toISOString().slice(0, 10)
}

/**
 * Fold stored rows into the per-day projection both surfaces render.
 *
 * This lives in the shared package, not in either consumer, because the thing
 * KTD6 is protecting against is not a type mismatch — it is the super-admin tab
 * and the `sj` verb quietly ranking, truncating or labelling the same day
 * differently. Sharing only the cutoff constant would still leave two folds to
 * drift; sharing the fold leaves none.
 *
 * Pure: no database, no clock, no I/O.
 */
function groupByDay(rows: BlackbaudUsageStoredRow[]): Map<string, BlackbaudUsageStoredRow[]> {
  const byDay = new Map<string, BlackbaudUsageStoredRow[]>()
  for (const row of rows) {
    const day = toDayKey(row.usageDate)
    const existing = byDay.get(day)
    if (existing) existing.push(row)
    else byDay.set(day, [row])
  }
  return byDay
}

/**
 * Rank a day's endpoints, highest first.
 *
 * Ties break on the endpoint name so the order is stable across reads — two
 * surfaces showing the same day must show the same list, and an unstable sort
 * would make them disagree intermittently, which is far harder to notice than
 * disagreeing always.
 */
function rankEndpoints(counted: BlackbaudUsageStoredRow[]): BlackbaudUsageEndpoint[] {
  const byEndpoint = new Map<string, number>()
  for (const row of counted) {
    byEndpoint.set(row.endpoint, (byEndpoint.get(row.endpoint) ?? 0) + row.requestCount)
  }
  return [...byEndpoint.entries()]
    .map(([endpoint, requestCount]) => ({ endpoint, requestCount }))
    .sort((a, b) => b.requestCount - a.requestCount || a.endpoint.localeCompare(b.endpoint))
}

function provenanceOf(hasLive: boolean, hasSeeded: boolean): BlackbaudUsageProvenance {
  if (hasSeeded && hasLive) return 'mixed'
  return hasSeeded ? 'seeded' : 'live'
}

function projectOneDay(date: string, dayRows: BlackbaudUsageStoredRow[], hasQuota: boolean): BlackbaudUsageDay {
  const totalRequests = dayRows.reduce((total, row) => total + row.requestCount, 0)

  // The loss-marker row exists only to carry a flag; it is not an endpoint
  // anyone called, and showing it in a breakdown would be a lie.
  const counted = dayRows.filter(row => row.requestCount > 0 && row.endpoint !== BLACKBAUD_USAGE_LOSS_MARKER_ENDPOINT)

  const ranked = rankEndpoints(counted)
  const hasSeeded = counted.some(row => row.source === 'SEEDED')

  return {
    date,
    totalRequests,
    quotaPercent: hasQuota ? Math.round((totalRequests / BLACKBAUD_DAILY_QUOTA) * 1000) / 10 : null,
    topEndpoints: ranked.slice(0, BLACKBAUD_USAGE_TOP_ENDPOINTS),
    otherRequests: ranked.slice(BLACKBAUD_USAGE_TOP_ENDPOINTS).reduce((sum, entry) => sum + entry.requestCount, 0),
    provenance: provenanceOf(
      counted.some(row => row.source === 'LIVE'),
      hasSeeded
    ),
    // Two independent reasons a figure is a floor, labelled differently by the
    // surfaces: writes were observed lost (R20), or the day was reconstructed
    // from logs and can only ever be a floor (R14).
    isLowerBound: dayRows.some(row => row.isLowerBound) || hasSeeded,
    isFinalized: dayRows.every(row => row.finalizedAt !== null)
  }
}

/**
 * Fold stored rows into the per-day projection both surfaces render.
 *
 * This lives in the shared package, not in either consumer, because the thing
 * KTD6 is protecting against is not a type mismatch — it is the super-admin tab
 * and the `sj` verb quietly ranking, truncating or labelling the same day
 * differently. Sharing only the cutoff constant would still leave two folds to
 * drift; sharing the fold leaves none.
 *
 * Pure: no database, no clock, no I/O.
 */
export function projectBlackbaudUsageDays(
  rows: BlackbaudUsageStoredRow[],
  options: { hasQuota: boolean }
): BlackbaudUsageDay[] {
  return (
    [...groupByDay(rows)]
      .map(([date, dayRows]) => projectOneDay(date, dayRows, options.hasQuota))
      // Most recent first — the question is almost always about now.
      .sort((a, b) => b.date.localeCompare(a.date))
  )
}
